"use client"

import { useState } from "react"
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { signUp, signIn, oauthGoogle, checkUsername } from "@/app/actions"
import { useTransition } from "react";
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

export default function AuthModal({ open, onOpenChange }) {
    const [mode, setMode] = useState("login")
    const [form, setForm] = useState({ email: "", username: "", password: "", passwordValidated: "" })
    const [infoMessage, setInfoMessage] = useState("")
    const [errorMessage, setErrorMessage] = useState("")
    const [isPending, startTransition] = useTransition();

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value })
    }

    const handleGoogle = async (e) => {
        e.preventDefault();

        if (!form.username) {
            setErrorMessage("Please enter a username to continue")
            return;
        }

        const valid = await checkUsername(form.username);
        if (!valid) {
            setErrorMessage("Username is taken, please try a different username.")
            return;
        }

        startTransition(async () => {

            const authWindow = window.open("", "GoogleAuthWindow", "width=800,height=600");

            if (!authWindow) {
                setErrorMessage("Popup was blocked. Please allow popups for this site.");
                return;
            }

            const handleMessage = (event) => {
                if (event.data?.type === 'AUTH_COMPLETE') {
                    setTimeout(() => {
                        authWindow.close();
                        handleOnOpenChange(false);
                    }, 500);
                }
            };

            window.addEventListener('message', handleMessage);

            const { data, error } = await oauthGoogle({ signUp: mode === "signup", form });
            if (error) {
                setErrorMessage(error.message);
                authWindow.close();
            }

            if (data.url) {
                if (data.error) {
                    authWindow.close();
                } else {
                    authWindow.location = data.url;
                }
            }
        });
    }

    const handleLoginSignup = async (e) => {
        e.preventDefault();
        let func = mode === "login" ? signIn : signUp;

        form.originURL = window.location.href;

        let { data, error } = await func(form);

        if (error) {
            setErrorMessage(error)
            return;
        }

        if (!data.session && !error && data.user) {
            setInfoMessage("Check your email for a confirmation link.")
            return;
        }

        const accessToken = data.session.access_token;
        const refreshToken = data.session.refresh_token;
        const expiresIn = data.session.expires_in;

        const supabase = createClient();

        let { data: _data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
        });

        if (sessionError) {
            setErrorMessage(sessionError.message)
            return;
        }

        onOpenChange(false);
    }

    const handleUsernameCheck = async () => {
        if (!form.username)
            return;

        const valid = await checkUsername(form.username);
        if (!valid) {
            setErrorMessage("Username is taken, please try a different username.")
            return;
        }

        setErrorMessage("")
    }

    const handleOnOpenChange = (bool) => {
        onOpenChange(bool)
        if (!bool) {
            setForm({ email: "", username: "", password: "", passwordValidated: "" })
            setErrorMessage("")
            setInfoMessage("")
        }
    }

    return (
        <Dialog open={open} onOpenChange={handleOnOpenChange}>
            <DialogContent className="sm:max-w-md [&>button:last-child]:hidden">
                <Tabs defaultValue="login" className="w-full">
                    <TabsList className="w-full">
                        <TabsTrigger onClick={() => setMode("login")} value="login">Login</TabsTrigger>
                        <TabsTrigger onClick={() => setMode("signup")} value="signup">Sign Up</TabsTrigger>
                    </TabsList>
                </Tabs>
                <form className="flex flex-col gap-2" onSubmit={handleLoginSignup}>
                    {mode === "signup" && (
                        <Input
                            name="username"
                            type="username"
                            placeholder="Username"
                            value={form.username}
                            onChange={handleChange}
                            onBlur={handleUsernameCheck}
                            onClick={() => setErrorMessage("")}
                            required
                        />
                    )}
                    <Input
                        name="email"
                        type="email"
                        placeholder="Email"
                        value={form.email}
                        onChange={handleChange}
                        required
                    />
                    <Input
                        name="password"
                        type="password"
                        placeholder="Password"
                        value={form.password}
                        onChange={handleChange}
                        required
                    />
                    {mode === "signup" && (
                        <Input
                            name="passwordValidated"
                            type="password"
                            placeholder="Confirm password"
                            value={form.passwordValidated}
                            onChange={handleChange}
                            required
                        />
                    )}

                    <div className={`text-sm text-center transition-all
                    ${errorMessage ? 'text-red-500' : 'text-green-500'}
                    ${errorMessage || infoMessage ? 'opacity-100' : 'opacity-0'}`}>
                        {infoMessage || errorMessage || '\u00A0'}
                    </div>

                    <div className="flex flex-col gap-4">
                        <Button type="submit" className="w-full">
                            {mode === "login" ? "Login" : "Sign Up"}
                        </Button>
                        <div className="text-center text-sm text-gray-500">or</div>
                        {mode === "signup" && <Input
                            name="username"
                            type="username"
                            placeholder="Username"
                            value={form.username}
                            onChange={handleChange}
                            onBlur={handleUsernameCheck}
                            required
                        /> }
                        <Button type="default" variant="outline" className="w-full" onClick={handleGoogle}>
                            {`${mode === "login" ? "Login" : "Sign Up"} with Google`}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    )
}
